import { PureComponent } from 'react';
import { batch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Actions } from '../../../../store/actions';
import { GlobalState } from '../../../../store/reducers';
import { PatientDetailsMR } from '../../../../store/reducers/prescriptionPad';
import { LabVitalEntity } from '../../../../store/types/prescription';
import MedicalRecordsEmpty, { NavBar } from './MedicalRecordsEmpty';
import MedicalRecord from '../../../../pages/MedicalRecord';
import { cachePatientDetails, upsertLabVital } from '../../../../store/actions/prescriptionPad';
import { notification } from 'antd';
import { UIDocumentEntity } from '../../../../pages/MedicalRecordsRightPanel';
import { onMedicalRecordDelete } from '../../../../pages/utils';
import { requestMedicalRecords } from '../../../../api/requestMedicalRecords';
import { track } from '../../../../tracking';

type Props = {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    mr?: PatientDetailsMR;
    allPtIds?: string[];
    hideActionCTAs?: boolean;
    ptId: string;
    profileFilterValue?: 'profile' | 'all';
    onRefresh?: () => void;
    docId: string;
};

type State = {
    profileFilterValue: 'profile' | 'all';
};
class MedicalRecordCard extends PureComponent<Props, State> {
    state: State = {
        profileFilterValue: this.props.profileFilterValue || 'profile',
    };

    copyToPrescription = (labVitals: LabVitalEntity[]) => {
        track('COPY_SMART_VITALS_TO_RX', { from: 'prescription-pad-patient-overview' });

        const { dispatch } = this.props;
        batch(() => {
            labVitals.forEach((l) => dispatch(upsertLabVital(l)));

            notification.success({
                message: `Copied to Prescription`,
                duration: 5,
            });
        });
        return;
    };

    onComplete = () => {
        const { dispatch, ptId } = this.props;
        ptId && dispatch(cachePatientDetails(ptId, true, true));
    };

    onRecordDelete = async (doc: UIDocumentEntity) => {
        try {
            await onMedicalRecordDelete(doc.document_id, doc._pt_oid);
            this.onComplete();
        } catch (e) {
            alert('Failed to delete record');
        }
    };

    onProfileFilterChange = (value: 'profile' | 'all') => {
        this.setState({ profileFilterValue: value });
        return;
    };

    onClickRequestRecordsBt = () => {
        const { ptId, docId } = this.props;
        ptId && docId && requestMedicalRecords(docId, ptId);
    };

    render() {
        const { mr, allPtIds, hideActionCTAs, ptId, onRefresh } = this.props;
        const { profileFilterValue } = this.state;

        if (!mr || !mr.data || mr.data.length === 0) {
            return (
                <MedicalRecordsEmpty
                    ptId={ptId || ''}
                    onComplete={this.onComplete}
                    lastUpdated={mr?.lastUpdatedAt || ''}
                    onClickRequestRecordsBt={this.onClickRequestRecordsBt}
                    hideActionCTAs={hideActionCTAs}
                    fetchState={mr?.state}
                    onRefresh={onRefresh}
                />
            );
        }

        const ptIds = new Set(allPtIds);

        let list = mr?.data;
        let diffCount = 0;

        if (profileFilterValue === 'profile') {
            list = list.filter((item) => ptIds?.has(item.oid || ''));
            diffCount = mr?.data.length - list.length;
        } else {
            diffCount = mr?.data.length - list.filter((item) => ptIds?.has(item.oid || '')).length;
        }

        return (
            <div className="flex flex-col rounded-8 font-lato bg-white w-full h-full pb-16">
                <NavBar
                    ptId={ptId || ''}
                    lastUpdated={mr?.lastUpdatedAt || ''}
                    isButtonVisible={true}
                    onComplete={this.onComplete}
                    onProfileFilterChange={this.onProfileFilterChange}
                    profileFilter={profileFilterValue}
                    diffCount={diffCount}
                    onClickRequestRecordsBt={this.onClickRequestRecordsBt}
                    hideActionCTAs={hideActionCTAs}
                />

                <div className="flex-1 flex flex-wrap p-16  gap-16 overflow-scroll hide-scrollbar">
                    {list?.length ? (
                        list?.map((item) => (
                            <div className="h-auto w-296">
                                <MedicalRecord
                                    documentFolder={item}
                                    copyToPrescription={this.copyToPrescription}
                                    containerCss="bg-darwin-neutral-50"
                                    onDelete={() => this.onRecordDelete(item)}
                                    hideActionCTAs={hideActionCTAs}
                                />
                            </div>
                        ))
                    ) : (
                        <MedicalRecordsEmpty
                            ptId={ptId || ''}
                            onComplete={this.onComplete}
                            lastUpdated={mr?.lastUpdatedAt || ''}
                            showNavbar={false}
                            onClickRequestRecordsBt={this.onClickRequestRecordsBt}
                            hideActionCTAs={hideActionCTAs}
                            fetchState={mr?.state}
                            onRefresh={onRefresh}
                        />
                    )}
                </div>
            </div>
        );
    }
}

export default MedicalRecordCard;
